import React, { useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';
import { useNotifications } from '../../contexts/NotificationContext';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'transaction', label: 'Transactions' },
  { id: 'fraud', label: 'Fraud' },
  { id: 'partner', label: 'Partners' },
  { id: 'subscription', label: 'Subscriptions' },
];

const TYPE_CONFIG = {
  transaction: {
    icon: 'Wallet',
    color: '#00E5FF',
    bg: 'rgba(0,229,255,0.12)',
  },
  fraud: {
    icon: 'ShieldAlert',
    color: '#FF4444',
    bg: 'rgba(255,68,68,0.12)',
  },
  partner: {
    icon: 'Building2',
    color: '#7B2FBE',
    bg: 'rgba(123,47,190,0.12)',
  },
  subscription: {
    icon: 'Crown',
    color: '#FFD700',
    bg: 'rgba(255,215,0,0.12)',
  },
};

function getSeverityColor(type, severity) {
  if (type === 'fraud') {
    if (severity === 'critical' || severity === 'high') return '#FF4444';
    if (severity === 'medium') return '#FF8C00';
    return '#FFD700';
  }
  return TYPE_CONFIG?.[type]?.color || '#00E5FF';
}

function getRelativeTime(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function NotificationItem({ notification, onMarkRead, onDelete }) {
  const config = TYPE_CONFIG?.[notification?.type] || TYPE_CONFIG?.transaction;
  const iconColor = getSeverityColor(notification?.type, notification?.severity);
  const isUnread = !notification?.is_read;

  return (
    <div
      className={`relative flex gap-3 p-3 rounded-lg transition-all duration-200 group hover:bg-[rgba(255,255,255,0.04)] ${
        isUnread ? 'border-l-2 border-[#00E5FF]' : 'border-l-2 border-transparent'
      }`}
    >
      {/* Icon */}
      <div
        className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5"
        style={{ background: config?.bg }}
      >
        <Icon name={config?.icon} size={16} color={iconColor} strokeWidth={2} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium leading-tight truncate ${
            isUnread ? 'text-[#F8FAFC]' : 'text-[#94A3B8]'
          }`}>
            {notification?.title}
          </p>
          <span className="flex-shrink-0 text-[10px] text-[#64748B] mt-0.5">
            {getRelativeTime(notification?.created_at)}
          </span>
        </div>
        <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2 leading-relaxed">
          {notification?.message}
        </p>
      </div>
      {/* Actions */}
      <div className="absolute right-2 top-2 hidden group-hover:flex items-center gap-1">
        {isUnread && (
          <button
            onClick={() => onMarkRead(notification?.id)}
            title="Mark as read"
            className="w-6 h-6 flex items-center justify-center rounded text-[#64748B] hover:text-[#00E5FF] hover:bg-[rgba(0,229,255,0.1)] transition-colors"
          >
            <Icon name="Check" size={12} strokeWidth={2.5} />
          </button>
        )}
        <button
          onClick={() => onDelete(notification?.id)}
          title="Delete"
          className="w-6 h-6 flex items-center justify-center rounded text-[#64748B] hover:text-[#FF4444] hover:bg-[rgba(255,68,68,0.1)] transition-colors"
        >
          <Icon name="X" size={12} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

export default function NotificationPanel() {
  const {
    notifications,
    unreadCount,
    isOpen,
    activeTab,
    loading,
    closePanel,
    setActiveTab,
    markRead,
    markAllRead,
    deleteNotification,
  } = useNotifications();

  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (panelRef?.current && !panelRef?.current?.contains(e?.target)) {
        // Check if click was on the bell button
        const bell = document.querySelector('[aria-label^="Notifications"]');
        if (bell && bell?.contains(e?.target)) return;
        closePanel();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, closePanel]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e?.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closePanel]);

  const filtered = notifications?.filter(n => {
    if (activeTab === 'all') return true;
    return n?.type === activeTab;
  }) || [];

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="fixed top-[68px] right-4 z-[200] w-[380px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-80px)] flex flex-col rounded-2xl overflow-hidden"
      style={{
        background: 'rgba(13,27,42,0.97)',
        border: '1px solid rgba(0,229,255,0.15)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,229,255,0.08), inset 0 1px 0 rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        animation: 'slideInPanel 0.2s cubic-bezier(0.4,0,0.2,1)',
      }}
      role="dialog"
      aria-label="Notifications panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(0,229,255,0.1)] flex-shrink-0">
        <div className="flex items-center gap-2">
          <Icon name="Bell" size={16} color="#00E5FF" strokeWidth={2} />
          <span className="text-sm font-semibold text-[#F8FAFC]">Notifications</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#00E5FF] text-[#0D1B2A]">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-[#00E5FF] hover:text-[#7FEFFF] transition-colors px-2 py-1 rounded hover:bg-[rgba(0,229,255,0.08)]"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={closePanel}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-[#64748B] hover:text-[#F8FAFC] hover:bg-[rgba(255,255,255,0.08)] transition-colors"
          >
            <Icon name="X" size={14} strokeWidth={2} />
          </button>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-[rgba(0,229,255,0.08)] flex-shrink-0 overflow-x-auto scrollbar-hide">
        {TABS?.map(tab => {
          const tabCount = tab?.id === 'all'
            ? notifications?.filter(n => !n?.is_read)?.length
            : notifications?.filter(n => n?.type === tab?.id && !n?.is_read)?.length;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150 ${
                activeTab === tab?.id
                  ? 'bg-[rgba(0,229,255,0.15)] text-[#00E5FF]'
                  : 'text-[#64748B] hover:text-[#94A3B8] hover:bg-[rgba(255,255,255,0.05)]'
              }`}
            >
              {tab?.label}
              {tabCount > 0 && (
                <span className={`text-[9px] font-bold px-1 rounded-full ${
                  activeTab === tab?.id ? 'bg-[#00E5FF] text-[#0D1B2A]' : 'bg-[rgba(255,255,255,0.1)] text-[#94A3B8]'
                }`}>
                  {tabCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Notification List */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,229,255,0.2) transparent' }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-[#64748B]">Loading notifications...</span>
          </div>
        ) : filtered?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[rgba(0,229,255,0.08)]">
              <Icon name="BellOff" size={22} color="#64748B" strokeWidth={1.5} />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-[#94A3B8]">No notifications</p>
              <p className="text-xs text-[#64748B] mt-1">
                {activeTab === 'all' ? "You're all caught up!" : `No ${activeTab} notifications`}
              </p>
            </div>
          </div>
        ) : (
          filtered?.map(notification => (
            <NotificationItem
              key={notification?.id}
              notification={notification}
              onMarkRead={markRead}
              onDelete={deleteNotification}
            />
          ))
        )}
      </div>
    </div>
  );
}
